import { Coins, IdCard, Layers } from 'lucide-react';
import { Breadcrumb } from '../../../../components/Breadcrumb';
import {
  SettingsMenu,
  type SettingsMenuEntry,
} from '../../../../components/settings/SettingsMenu';

/*
  What the organization module has to configure. One row per surface, each leading to its own
  page — see `<SettingsMenu>` for why the lists are not inlined here.

  Every row belongs to a DIFFERENT module, and each carries its own key: a caller who may
  maintain the position catalog but not the tier ladder sees one row, not a 403 behind the
  other. That is also why the page itself is gated on holding ANY of them, rather than on one.
*/
const ENTRIES: SettingsMenuEntry[] = [
  {
    key: 'tiers',
    label: 'Tiers',
    description:
      'The ladder the chart draws units against — Division, Department, Section.',
    icon: Layers,
    to: '/organization/settings/tiers',
    permission: 'org-unit-types.read',
  },
  {
    key: 'positions',
    label: 'Positions',
    description: 'The catalog of position titles the staffing pattern draws its posts from.',
    icon: IdCard,
    to: '/organization/settings/positions',
    permission: 'positions.read',
  },
  /*
    Eligibilities is NOT here, and that is a move rather than an omission. A position's
    qualification standards still name one, so this menu has a claim on it — but CS Form 212
    section IV files an employee's earned eligibilities under the same catalog, which is the
    heavier consumer, and one catalog reached from two menus is two breadcrumbs disagreeing
    about where the reader came from. It lives under Personnel → Settings.
  */
  {
    /*
      The schedule is a reference catalog like the three above it, maintained on its own
      cadence and read by the plantilla rather than owned by it — so it belongs in this menu
      rather than as a sidebar entry of its own. `salary-grades.read` is its own key for the
      same reason each row above has one: reading the schedule and reading the staffing
      pattern are different grants.
    */
    key: 'salary-grades',
    label: 'Salary Grades',
    description: "The salary schedule a plantilla position's grade is read against.",
    icon: Coins,
    to: '/organization/settings/salary-grades',
    permission: 'salary-grades.read',
  },
];

/**
 * Organization → Settings: a **menu of what the module configures**, not the settings
 * themselves.
 *
 * The sidebar carries one entry per module rather than one per configurable list, and this
 * page is where that entry expands — an extension of the sidebar. Each row leads to a full
 * page for that list, with its own breadcrumb, toolbar and settings cog.
 */
export function OrgSettingsPage() {
  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-50">
      <Breadcrumb segments={['Human Resources', 'Organization', 'Settings']} />

      <div className="modern-scrollbar min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto flex max-w-3xl flex-col gap-3">
          {/*
            One line, and it says the thing the menu cannot: where the records went. Without
            it a reader who came looking for units reads the two rows and concludes the
            module has nothing else.
          */}
          <p className="text-xs text-slate-500">
            The catalogs the organization chart is built from. Units themselves are created and
            arranged on Manage.
          </p>

          <SettingsMenu entries={ENTRIES} />
        </div>
      </div>
    </div>
  );
}
